import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import materialSupplierService from "../../../../../services/web/material-supplier-service.js";
import {useQuery} from "@tanstack/react-query";
import SentryRow from "../../../../../components/template/sentry-row.jsx";

const MaterialSuppliersList = () => {
    const navigate = navigateHook();
    const gridRef = useRef();
    const [columnDefs, setColumnDefs] = useState([]);
    const [filter, setFilter] = useState("");
    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);
    const onColumnMoved = (params) => {
        const columns = params.api.getAllDisplayedColumns().map(col => col.colId);
        localStorage.setItem("material-suppliers-order-columns", JSON.stringify(columns))
    }

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    useEffect(() => {
        const columns = [
            {headerName: "Nome fantasia", field: "name", flex: 1, filter: "agTextColumnFilter",
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {headerName: "Razão social", field: "social_name", flex: 1, filter: "agTextColumnFilter",
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            {
                headerName: "CNPJ",
                field: "cnpj",
                flex: 1,
                valueFormatter: ({ value }) => {
                    if (!value) return "";

                    const digits = String(value).replace(/\D/g, "");
                    if (digits.length !== 14) return value;

                    return digits.replace(
                        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
                        "$1.$2.$3/$4-$5"
                    );
                },
            }
        ];

        const orderColumns = JSON.parse(localStorage.getItem("users-order-columns"));

        if (orderColumns) aggridUtils.sortColumns(columns, orderColumns);
        setColumnDefs(columns);
    }, []);

    const {data: suppliers} = useQuery({
        queryFn: async () => {
            const { data } = await materialSupplierService.list();
            return data;
        },
        queryKey: ['suppliers'],
    });

    const clearFilter = useCallback(() => {
        setFilter("");
        const api = gridRef.current?.api;
        if (api) {
            api.setGridOption(
                "quickFilterText",
                null,
            );
        }
    }, []);

    const search = useCallback(() => {
        gridRef.current.api.setGridOption(
            "quickFilterText",
            filter,
        );
    }, [filter]);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    const onRowClicked = ({event, data}) => {
        const id = data.id;
        const urlDev = `/materials/suppliers/form/${id}`;
        let isBlank = false
        if (event.ctrlKey) isBlank = true
        navigate(urlDev, isBlank)
    }

    const openForm = () => {
        const urlDev = `/materials/suppliers/form`;
        navigate(urlDev)
    }

    return (
        <Template>
            <Template.Header>
                <Template.Title>
                    Fornecedores
                </Template.Title>
                <Button onClick={openForm}>
                    <FaPlus/><span className="font-bold">Adicionar fornecedor</span>
                </Button>
            </Template.Header>
            <Template.Body className="space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className={"col-span-12 flex gap-4"}>
                                <InputForm classNameWrapper={"w-full"} id="search" value={filter} onChange={({target: {value}}) => setFilter(value)}
                                           label="Pesquisar"/>

                                <div className="flex justify-start gap-2">
                                    <Button target="text-white" title="Limpar" onClick={() => clearFilter()}>
                                        <FaEraser />
                                    </Button>

                                    <Button target="primary" title="Buscar" onClick={search}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="aggrid-row-pointer h-[calc(100vh-153px)]">
                            <AgGridReact
                                onColumnMoved={onColumnMoved}
                                ref={gridRef}
                                rowData={suppliers ?? []}
                                columnDefs={columnDefs}
                                pagination
                                onRowClicked={onRowClicked}
                                paginationPageSize={50}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={theme}
                                defaultColDef={defaultColDef}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
};

export default MaterialSuppliersList;